import { ref, h, watch } from 'vue'
import type { CodeLayoutSplitNInstance, CodeLayoutSplitNPanel, CodeLayoutInitialPanelConfig } from '@edanweis/vue-code-layout'
import type { Tool } from '@/types/tools'

import { useUserStore } from '@/stores/userStore'
import { useProjectStore } from '@/stores/projectStore'
import { storeToRefs } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import { useNuxtApp, useSupabaseClient, useFetch } from '#imports'

import { useRootPageRef } from '@/lib/utils'
import { domToPng } from 'modern-screenshot'
import { useUppyStore } from '@/stores/uppyStore'
import { useProjectData } from '@/composables/projectData'
import { useLayoutStore } from '@edanweis/vue-code-layout'

// Add Icon component import
import { Icon } from '#components'
import type { CodeLayoutSplitNPanelInternal } from '@edanweis/vue-code-layout'
import type { PanelData, PanelAttributes } from '@/types/projectdock'

// Add extended panel type to include the additional properties
interface ExtendedCodeLayoutSplitNPanel extends CodeLayoutSplitNPanel {
  iconSmallName?: string;
  iconLargeName?: string;
}

export interface LayoutMethods {
  saveLayout: () => Promise<void>
  loadLayout: (layout: any, arrangement: any) => Promise<void>
  addPanel: (tool: Tool, addToActiveGrid: boolean) => Promise<void>
  clearLayout: () => Promise<void>
  updatePanel: (options: any) => Promise<void>
  lastActivePanel: CodeLayoutSplitNPanelInternal | null,
  replacePanel: (panelId: string, options: any) => Promise<void>
} 

// Define interfaces for store refs to ensure id properties exist
interface ProjectWithId {
  id: string;
  [key: string]: any;
}

interface TeamWithId {
  id: string;
  [key: string]: any;
}

interface UserWithId {
  id: string;
  [key: string]: any;
}

export const useLayoutManager = () => {
  
  const layoutRef = ref<LayoutMethods | null>(null)
  const layoutStore = useLayoutStore()
  const client = useSupabaseClient()
  const userStore = useUserStore()
  const projectStore = useProjectStore()
  const uppyStore = useUppyStore()
  const { uploadSuccess } = storeToRefs(uppyStore)
  const { addThumbnailToLayout } = useProjectData()
  const { selectedTeam } = storeToRefs(userStore) as { selectedTeam: Ref<TeamWithId | null> }
  const { selectedProject } = storeToRefs(projectStore) as { selectedProject: Ref<ProjectWithId | null> }
  const { setSyncStatus } = projectStore
  const userRefs = storeToRefs(userStore)
  const user = userRefs.user as Ref<UserWithId | null>
  const hasChanges = ref(false)

  

  // Add emits object to expose events
  const events = reactive({
    onBeforeSave: () => setSyncStatus('saving'),
    onAfterSave: () => setSyncStatus('saved'),
    onBeforeLoad: () => setSyncStatus('loading'),
    onAfterLoad: () => setSyncStatus('idle'),
    onBeforeCreateVersion: () => setSyncStatus('saving'),
    onAfterCreateVersion: async (version: any) => {
      setSyncStatus('saved')
    },
    onBeforeLoadVersion: () => setSyncStatus('loading'),
    onAfterLoadVersion: () => setSyncStatus('idle'),
    onError: (error: Error) => {
      console.error('Layout error:', error)
      setSyncStatus('error')
    }
  })

  const setLayoutRef = (methods: LayoutMethods | null) => {
    layoutRef.value = methods
  }

  const setLayoutInstance = (instance: CodeLayoutSplitNInstance) => {
    try {
      layoutStore.setLayoutInstance(instance)
    } catch (err) {
      // If can't set instance due to being already initialized, 
      // try to reset the store first
      console.warn('Error setting layout instance, attempting reset:', err)
      resetLayoutStore()
      layoutStore.setLayoutInstance(instance)
    }
  }

  // Add a method to reset the layout store state
  const resetLayoutStore = (forceHardReset = false) => {
    try {
      console.log('Attempting to reset layout store', forceHardReset ? '(forced)' : '')
      
      if (forceHardReset) {
        console.log('Performing hard reset of layout store')
        // Replace the layout store reference entirely
        // @ts-ignore - Accessing internal API
        if (typeof layoutStore._reset === 'function') {
          // @ts-ignore - Access internal method
          layoutStore._reset()
          console.log('Layout store reset successfully using internal method')
        }

        // Also attempt manual cleanup
        try {
          // Safely check and reset the initialized property
          // @ts-ignore - Access internal state
          if (layoutStore.initialized && layoutStore.initialized.value) {
            // @ts-ignore - Access internal state
            layoutStore.initialized.value = false
          }
          
          if (layoutStore.layoutInstance) {
            // @ts-ignore - Access internal state  
            layoutStore.layoutInstance.value = null
          }
          
          // Reset versions too
          if (layoutStore.versions) {
            layoutStore.versions.value = []
          }
          
          console.log('Layout store properties manually reset')
        } catch (err) {
          console.warn('Error manually resetting layout store properties:', err)
        }
        
        // Clear any other internal state
        try {
          // @ts-ignore - Access internal state
          if (layoutStore._state) {
            // @ts-ignore
            layoutStore._state = {}
          }
        } catch (err) {
          console.warn('Could not reset internal state:', err)
        }
      } else {
        // Soft reset - just clear the initialization flag
        try {
          // Safely check and reset the initialized property
          // @ts-ignore - Access internal property
          if (layoutStore.initialized && layoutStore.initialized.value) {
            // @ts-ignore
            layoutStore.initialized.value = false
          }
          
          console.log('Layout store soft reset completed')
        } catch (err) {
          console.warn('Error during soft reset:', err)
        }
      }
    } catch (err) {
      console.error('Failed to reset layout store:', err)
    }
  }

  const replacePanel = (panelId: string, options: any) => {
    console.log('[LayoutManager] replacing panel', panelId, options)
    try {
      layoutStore.layoutInstance?.value?.replacePanel(panelId, options)
    } catch (err) {
      console.error('Error replacing panel:', err)
      // setSyncStatus('error')
    }
  }

  // Create shared panel config
  const createPanelConfig = (): CodeLayoutInitialPanelConfig => ({
    titleGenerator: (uniqueName: string) => 'Blah',
    // tooltipGenerator: (uniqueName: string) => 'New',
    iconGenerator: (iconName: string) => {
      console.log('iconGenerator called with:', iconName)
      return () => h(Icon, { 
        name: iconName,
        size: '16'
      })
    },
    dataGenerator: (uniqueName: string) => ({
      hasComponent: false,
      name: uniqueName
    })
  })

  const initializeLayout = async (stateId: string) => {
    await nextTick()
    console.log('initializeLayout', stateId)

    // Check if we have at least one valid ID
    const hasProjectId = !!selectedProject.value?.id
    const hasTeamId = !!selectedTeam.value?.id
    const hasUserId = !!user.value?.id
    
    if (!hasProjectId && !hasTeamId && !hasUserId) {
      console.warn('Cannot initialize layout: no valid IDs available (project, team, or user)')
      throw new Error('Missing required IDs for layout initialization')
    }

    const panelConfig = createPanelConfig()

    try {
      await layoutStore.initialize({
        supabase: client,
        stateId,
        additionalData: () => ({
          project_id: selectedProject.value?.id || null,
          group_id: selectedTeam.value?.id || null,
          user_id: user.value?.id || null,
          updated_at: new Date().toISOString()
        }),
        // Use the same config for both to ensure consistency
        defaultPanelConfig: panelConfig,
        initialPanelConfig: panelConfig,
        events,
        debug: true
      })
    } catch (error) {
      console.error('Failed to initialize layout:', error)
      // Reset the layout store to recover from initialization errors
      resetLayoutStore(true)
      throw error
    }
  }

  const saveLayout = async () => {
    try {
      // Check if we have at least one valid ID
      const hasProjectId = !!selectedProject.value?.id
      const hasTeamId = !!selectedTeam.value?.id
      const hasUserId = !!user.value?.id
      
      if (!hasProjectId && !hasTeamId && !hasUserId) {
        console.warn('Cannot save layout: no valid IDs available (project, team, or user)')
        throw new Error('Missing required IDs for layout save')
      }
      
      // Ensure we have a layout instance before saving
      if (!layoutStore.layoutInstance?.value) {
        console.warn('No layout instance available, cannot save layout')
        throw new Error('No layout instance available')
      }
      
      await layoutStore.saveState()
    } catch (err) {
      console.error('Error saving layout:', err)
      setSyncStatus('error')
      throw err // Re-throw to allow caller to handle
    }
  }

  const loadLayout = async (layout: any, arrangement?: any) => {
    try {
      // console.log('loading layout', layout, arrangement)
      await layoutStore.loadVersion(layout, arrangement)
      
      // Force panels to be visible after loading
      await ensurePanelsVisible()
    } catch (err) {
      console.error('Error loading layout:', err)
      setSyncStatus('error')
    }
  }

  const ensurePanelsVisible = async () => {
    await nextTick()
    const instance = layoutStore?.layoutInstance?.value
    if (!instance) {
      console.warn('No layout instance available, cannot ensure panels visible')
      return
    }
    
    try {
      // Get all panels
      const allPanels = layoutStore.getAllPanels?.() || []
      console.log(`Ensuring visibility of ${allPanels.length} panels`)
      
      // Force all panels to be visible and open
      allPanels.forEach(panel => {
        if (!panel) return
        
        try {
          // @ts-ignore - Property may not exist on type
          panel.visible = true
          // @ts-ignore - Property may not exist on type
          panel.open = true
        } catch (e) {
          console.warn('Error setting panel visibility:', e)
        }
      })
      
      // Force relayout
      const rootGrid = instance.getRootGrid?.()
      if (rootGrid) {
        rootGrid.notifyRelayout?.()
      }
    } catch (err) {
      console.error('Error ensuring panels visible:', err)
    }
  }

  // Add a more robust way to safely get all panels
  const getAllPanels = () => {
    try {
      return layoutStore?.getAllPanels?.() || []
    } catch (err) {
      console.warn('Error getting all panels:', err)
      return []
    }
  }

  const updatePanel = async (panel: any, newData: any) => {
    const {data, name} = panel
    
    try {
      const instance = layoutStore.layoutInstance?.value;
      if (!instance) {
        console.error('No layout instance found');
        return;
      }
      instance.updateData(name, newData)
      
      
    } catch (err) {
      console.error('Error updating panel:', err);
      setSyncStatus('error');
    }
  }

  const addPanel = async (options: any) => {
    const {tool, addToActiveGrid = true, data = {}, name = uuidv4()} = options
    try {
      const instance = layoutStore.layoutInstance?.value
      if (!instance) {
        console.error('No layout instance found')
        return null
      }

      // Create panel data according to type
      const panelData: PanelData = {
        title: tool.label,
        hasComponent: true,
        iconSmall: tool.icon,
        iconSmallName: tool.icon,
        iconLargeName: tool.icon,
        tool_uid: tool.uid,
        tool: tool,
        running: false,
        connections: [],
        output: undefined,
        ...tool.defaultData || {},
        ...data
      }

      // Create panel attributes
      const panelAttributes: PanelAttributes = {
        title: tool.label,
        hasComponent: true,
        iconSmall: tool.icon,
        iconSmallName: tool.icon,
        iconLargeName: tool.icon,
        // tooltip: tool.label,
        closeType: 'unSave',
        label: tool.label,
        showBadge: false,
        tool_uid: tool.uid
      }

      // Create unique name if one with this name already exists
      let panelName = options.name || uuidv4()
      // Check if panel with this name already exists
      if (instance.getPanelByName(panelName)) {
        console.warn(`Panel with name ${panelName} already exists, generating a new name`)
        panelName = uuidv4()
      }

      // Create the panel according to CodeLayoutSplitNPanel type
      const panel: ExtendedCodeLayoutSplitNPanel = {
        name: panelName,
        title: panelAttributes.title || '',
        // tooltip: panelAttributes.tooltip || '',
        closeType: panelAttributes.closeType as any,
        iconSmall: () => h(Icon, { name: panelAttributes.iconSmall || '' }),
        iconSmallName: panelAttributes.iconSmallName,
        iconLargeName: panelAttributes.iconLargeName,
        data: panelData
      }

      try {
        const activeGrid = instance.getActiveGrid()
        if (!activeGrid) {
          console.warn('No active grid found, adding panel to root grid')
          const rootGrid = instance.getRootGrid()
          if (!rootGrid) {
            console.error('No root grid found')
            return null
          }
          
          const addedPanel = rootGrid.addPanel(panel)
          console.log('addedPanel added to root grid', addedPanel)
          
          if (addedPanel) {
            // Initialize the panel if needed
            if (addedPanel.init) {
              addedPanel.init()
            }
            
            // Ensure panel is visible
            addedPanel.visible = true
            addedPanel.open = true
            
            // Set as active panel
            rootGrid.setActiveChild(addedPanel as any)
            rootGrid.notifyRelayout()
            
            console.log('Panel added to root grid and activated', addedPanel)
            return addedPanel
          }
        } else {
          console.log('adding panel to active grid', activeGrid)
          const addedPanel = activeGrid.addPanel(panel)
          
          if (addedPanel) {
            // Initialize the panel if needed
            if (addedPanel.init) {
              addedPanel.init()
            }
            
            // Ensure panel is visible
            addedPanel.visible = true
            addedPanel.open = true
            
            // Set as active panel in the grid
            activeGrid.setActiveChild(addedPanel as any)
            activeGrid.notifyRelayout()
            
            console.log('Panel added to active grid and activated', addedPanel)
            return addedPanel
          } else {
            console.warn('Failed to add panel to active grid')
          }
        }
      } catch (err) {
        console.error('Error managing grid operations:', err)
        throw err // Re-throw to let caller handle
      }

      return null
    } catch (err) {
      console.error('Error adding panel:', err)
      return null
    }
  }

  const createVersion = async (versionName: string) => {
    try {
      // Check if we have at least one valid ID
      const hasProjectId = !!selectedProject.value?.id
      const hasTeamId = !!selectedTeam.value?.id
      const hasUserId = !!user.value?.id
      
      if (!hasProjectId && !hasTeamId && !hasUserId) {
        console.warn('Cannot create version: no valid IDs available (project, team, or user)')
        throw new Error('Missing required IDs for version creation')
      }
    
      console.log('creating version', versionName)
      
      // Ensure we have a layout instance before creating a version
      if (!layoutStore.layoutInstance?.value) {
        console.warn('No layout instance available, cannot create version')
        throw new Error('No layout instance available')
      }
      
      await layoutStore.createVersion(versionName)
    } catch (err) {
      console.error('Error creating version:', err)
      setSyncStatus('error')
      throw err // Re-throw to allow caller to handle
    }
  }

  const deleteVersion = async (versionId: string) => {
    try {
      // Delete from layouts_state_versions table
      const { error: deleteError } = await client
        .from('layout_state_versions')
        .delete()
        .eq('id', versionId)

      if (deleteError) throw deleteError

      // Update local state by removing the version
      layoutStore.versions.value = layoutStore.versions.value.filter(v => v.id !== versionId)

      // Show success notification
      push.success({
        title: 'Version deleted',
        message: 'Layout version has been permanently deleted'
      })
    } catch (err) {
      console.error('Error deleting version:', err)
      push.error({
        title: 'Error',
        message: 'Failed to delete layout version'
      })
      setSyncStatus('error')
    }
  }

  const clearLayout = async () => {
    try {
      layoutStore.layoutInstance?.value.clearLayout({ leaveEmptyGrid: true });
      layoutStore.layoutInstance?.value.getRootGrid().notifyRelayout()
      console.log('layoutStore.layoutInstance?.value', layoutStore.layoutInstance?.value)
      console.log('getGridTreeDebugText', layoutStore.layoutInstance?.value?.getGridTreeDebugText())

      setSyncStatus('idle');
    } catch (err) {
      console.error('Error clearing layout:', err);
      setSyncStatus('error');
    }
  }

  const clearLayoutAndState = async () => {
    let layout = layoutStore.layoutInstance?.value;
    try {
      if (layout) {
        layout.clearLayout({ leaveEmptyGrid: true });
      }
    } catch (error) {
      console.error('Failed to clear layout:', error);
      push.error({
        title: 'Error',
        message: 'Failed to clear layout'
      });
    }

    console.log(`debug grid`, layout?.getGridTreeDebugText())
    console.log(`layoutStore.layoutInstance`, layoutStore.layoutInstance?.value)
  };

  const lastActivePanel = () => {
    return layoutStore.layoutInstance?.value?.activePanel || null;
  }

  // Add a method to properly reset and reinitialize the layout system
  const resetAndReinitialize = async (stateId: string, instance: CodeLayoutSplitNInstance) => {
    console.log('Performing complete reset and reinitialization')
    
    try {
      // First do a hard reset of the layout store
      resetLayoutStore(true)
      
      // Wait for any async operations to complete
      await nextTick()
      
      // Try to reinitialize with the new instance
      try {
        // Set the layout instance - this might fail if the store thinks it's initialized
        setLayoutInstance(instance)
      } catch (err) {
        console.warn('Could not directly set layout instance, using manual approach', err)
        
        // Manually set the instance using internal properties
        try {
          // @ts-ignore - Access internal state
          if (layoutStore.layoutInstance) {
            // @ts-ignore
            layoutStore.layoutInstance.value = instance
          }
          console.log('Manually set layoutStore.layoutInstance')
        } catch (innerErr) {
          console.error('Failed to manually set layout instance:', innerErr)
        }
      }
      
      // Now try to initialize the layout
      try {
        await initializeLayout(stateId)
        console.log('Layout reinitialized successfully')
      } catch (err) {
        console.error('Failed to reinitialize layout:', err)
        
        // Last resort: try to clear any internal state that would block initialization
        try {
          // @ts-ignore - Access internal properties
          if (layoutStore._state) {
            // Clear specific blocking properties
            // @ts-ignore
            delete layoutStore._state.initialized
            // @ts-ignore
            delete layoutStore._state.stateId
            console.log('Manually cleared internal initialization state')
          }
          
          // Try initialization one more time
          await initializeLayout(stateId)
          console.log('Layout initialization succeeded after manual state cleanup')
        } catch (finalErr) {
          console.error('Final attempt to initialize layout failed:', finalErr)
          throw finalErr
        }
      }
      
      return true
    } catch (err) {
      console.error('resetAndReinitialize failed:', err)
      throw err
    }
  }

  return {
    layoutRef,
    layoutStore,
    setLayoutRef,
    setLayoutInstance,
    initializeLayout,
    saveLayout,
    loadLayout,
    addPanel,
    createVersion,
    deleteVersion,
    clearLayout,
    clearLayoutAndState,
    events,
    updatePanel,
    lastActivePanel,
    replacePanel,
    ensurePanelsVisible,
    resetLayoutStore,
    getAllPanels,
    resetAndReinitialize
  }
} 